import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Flag, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ReportMessageModalProps {
  messageId: string;
  onClose: () => void;
}

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or unwanted messages' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'scam', label: 'Scam or fraud' },
  { value: 'other', label: 'Other (please specify)' }
];

export const ReportMessageModal = ({ messageId, onClose }: ReportMessageModalProps) => {
  const { toast } = useToast();
  const [reason, setReason] = useState('spam');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason || (reason === 'other' && !description.trim())) {
      toast({
        title: 'Missing information',
        description: 'Please provide a reason for the report',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);

    try {
      // Obtener información del usuario actual
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Not authenticated');

      // Obtener el mensaje para obtener chat_room_id y sender
      const { data: message, error: msgError } = await supabase
        .from('match_chat_messages')
        .select('chat_room_id, sender_user_id')
        .eq('id', messageId)
        .single();

      if (msgError) throw msgError;

      // Crear reporte - usando campos disponibles en el schema
      const reportDescription = `Reason: ${reason}\n\n${description || 'No additional details provided'}`;
      
      const { error: reportError } = await supabase.from('user_reports').insert([{
        reporter_user_id: user.id,
        reported_user_id: message.sender_user_id,
        chat_room_id: message.chat_room_id,
        message_id: messageId,
        description: reportDescription,
        report_type: reason, // Añadir report_type
        status: 'pending'
      }]);

      if (reportError) throw reportError;

      toast({
        title: 'Report submitted',
        description: 'Thank you for helping keep our community safe. We will review this report.'
      });

      onClose();
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast({
        title: 'Error submitting report',
        description: error.message || 'Could not submit report',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            Report Message
          </DialogTitle>
          <DialogDescription>
            Help us understand what's wrong with this message
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              False reports may result in action against your account. Please only report content that violates our community guidelines.
            </p>
          </div>

          {/* Reason Selection */}
          <div className="space-y-3">
            <Label>Reason for report</Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              {REPORT_REASONS.map(r => (
                <div key={r.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={r.value} id={r.value} />
                  <Label htmlFor={r.value} className="font-normal cursor-pointer">
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Additional details {reason === 'other' && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more context about why you're reporting this message..."
              maxLength={500}
              rows={4}
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/500
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (reason === 'other' && !description.trim())}
            className="flex-1 bg-red-500 hover:bg-red-600"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
